import {
  ACTION,
  BOTS,
  LABEL
} from '../analytics/constants';

declare global {
  interface Window {
    gaGlobal: Record<string, unknown>; // {} object
    Intercom: (...args: unknown[]) => void; // functions w. spread syntax because args may vary

    dataLayer: unknown[];  // [] object
    _hsq: unknown[];

    analytics: {
      ready: (...args: unknown[]) => void;
      track: (...args: unknown[]) => void;
    }; // {} object w. several .methods() (= functions)
  }
}

/* GENERAL PURPOSE */

export const isBot = () => BOTS.some(i => window.navigator.userAgent?.toLowerCase()?.includes(i));

export const appendScript = (
  src: string, 
  props: Record<string, unknown> = {}, // optional bc {} fallback value provided
  callback?: (...args: unknown[]) => void // ?: = optional
) => {
  const script = document.createElement('script');
  /^(http|\/\/)/.test(src) ? script.src = src : script.innerHTML = src;
  Object.keys(props).map(k => script.setAttribute(k, props[k] as string));
  document.head.appendChild(script);
  if (callback) script.onload = callback;
};

export const loadYoutubePlayer = (id: string) => {
  const settings = { videoId: id, width: '100%', height: '100%'};
  const playerVars = {playsinline: 1, origin: window.location.host};
  // const playerEvents = {onReady: percentWatched, onStateChange: playerState};

  const youtube = window['YT' as keyof typeof window];
  new youtube.Player('yt-player', {playerVars: playerVars, ...settings});
};

/*
pass URL params between domain and subdomain
JSON.parse(document.cookie.match(`(^|;)\\s*${MARKETING_COOKIE}\\s*=\\s*([^;]+)`)?.pop() || '{}');
*/
export const appendUrlParams = (url: string) => {
  const props = { ...window?.localStorage };
  const params = Object.keys(props).filter(i => /fbclid|li_fat_id|gclid|utm_/.test(i));
  const append = params.map((i: string) => `${i}=${props[i]}`).join('&');
  return append ? url + (url.includes('?') ? '&' : '?') + append : url;
};


/* HUBSPOT */

// sets 'Original Source' in contact props
export const hubspotTrack = (email: string, event: string) => {
  window._hsq && window._hsq.push(['identify', { email: email }]);
  window._hsq && window._hsq.push(['trackEvent', { id: event }]);
};


/* FORM SUBMIT */

export const obscureString = (str: string) => {
  if (!str) return undefined;
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  const getRandomChar = () => chars[Math.floor(Math.random() * chars.length)];
  const swapChar = (str: string) => (/^[^\w]+$/i.test(str) ? str : getRandomChar());
  const applySwap = (str: string) => str.split('').map(i => (i = swapChar(i))).join('');

  const isEmail = /^[^@]*@[^.@]*\.[A-Za-z0-9.]+$/i.test(str);
  if (isEmail) {
    const parts = str.split('@');
    const obscuredUsername = applySwap(parts[0]);
    const obscuredDomain = parts[1].split('.').map(i => applySwap(i)).join('.');
    return `${obscuredUsername}@${obscuredDomain}`;
  } else {
    return applySwap(str);
  }
};

export const applyFuncToObjectValues = (
  obj: Record<string, unknown>,
  func: (val: string) => void
) => {
  const entries = Object.entries(obj);
  const process = entries.map(([k, v]) => [k, func(v as string)]);
  return Object.fromEntries(process);
};

export const formSubmitTrack = (props: Record<string, unknown> & {
  paidSocialEvents: Record<string, Record<string, unknown>>;
  facebookPayload: Record<string, unknown>;
}) => {

  const label = props.label as string;
  const adSpend = props.adSpend as string;
  const facebookPayload = props.facebookPayload;
  const {Facebook, Linkedin, Bing} = props.paidSocialEvents;
  
  const [facebookEvent, linkedinEvent, bingEvent] = /^P[2-8]$/.test(adSpend)
    ? [Facebook.MQL, Linkedin.MQL, Bing.MQL]
    : [Facebook.noMQL, Linkedin.noMQL, Bing.noMQL]


  const obscuredFacebookPayload = applyFuncToObjectValues(facebookPayload, obscureString);

  const leadEvents: string[] = [
    LABEL.Demo,
    LABEL.Signup,
    LABEL.Prism,
    LABEL.Startup,
    LABEL.AMC,
    LABEL.BI
  ];

  const defaults = {
    label: label,
    category: leadEvents.includes(label) ? `${label} ${bingEvent}` : bingEvent,
    facebookPayload: {
      eventName: facebookEvent, 
      ...obscuredFacebookPayload
    }
  };

  const payload = adSpend
    ? {...defaults, ...{adSpend: adSpend}}
    : defaults;
  
  /*
  . sGTM DL push - V2, payload structure is important
  . leadUserData is sent using Google object format / field names
  . formLabel and adSpend (if any) are sent as custom event attributes
  */
  const defaultDLPayload = {
    event: 'isFormSubmitted',
    formLabel: payload.label,
    leadUserData: {
      email: payload.facebookPayload.em,
      phone_number: payload.facebookPayload.ph,
      address: {
        first_name: payload.facebookPayload.fn,
        last_name: payload.facebookPayload.ln,
        country: payload.facebookPayload.country,
        postal_code: '12345',
      },
    }
  };

  const customDLPayload = adSpend
  ? {...defaultDLPayload, ...{formAdSpend: adSpend}}
  : defaultDLPayload;

  window?.dataLayer.push(customDLPayload);
  // end of sGTM DL push

  facebookPayload.em && hubspotTrack(facebookPayload.em as string, ACTION.FormSubmitted);
};


/* SEGMENT TRACKING CALLS - remove here and across the code base */
export const clientId = () => window.gaGlobal?.vid || '';

export const segmentTrack = (event: string, props: Record<string, unknown>) => {
  window.analytics?.ready(() => {
    window.analytics.track(event, {
      url: window?.location.href || '', // for debug, GA4 auto captures 'page_location'
      clientId: clientId(),
      ...props
    });
  });
};